import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

type DailyStat = {
  date: string;
  revenue: number;
  cash: number;
};

type Props = {
  dailyStats: DailyStat[];
};

export default function ProfitLossChart({ dailyStats }: Props) {
  const chartData = useMemo(() => {
    return dailyStats.map((item) => {
      const revenue = item.revenue;
      const cost = Math.round(item.cash / 6);
      const profit = revenue - cost;
      const profitMarginValue = revenue > 0 ? (profit / revenue) * 100 : 0;
      const date = new Date(item.date);

      return {
        date: `${date.getMonth() + 1}/${date.getDate()}`,
        fullDate: item.date, // 툴팁에서 전체 날짜 표시용
        profitMargin: profitMarginValue,
        revenue: revenue / 1000, // k 단위로 변환
      };
    });
  }, [dailyStats]);

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0',
        borderRadius: 3,
        p: 2,
        mt: 3,
      }}
    >
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
        Statistics
      </Typography>
      <Box sx={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <defs>
              <linearGradient id="colorProfitMargin" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#4CAF50" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF9800" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#FF9800" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="date" tick={{ fill: '#000', fontSize: 12 }} />
            <YAxis
              yAxisId="left"
              tick={{ fill: '#000', fontSize: 12 }}
              tickFormatter={(value: number) => `${value}%`}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fill: '#000', fontSize: 12 }}
              tickFormatter={(value: number) => `${value}k`}
            />
            <RechartsTooltip
              labelFormatter={(label, payload) => {
                if (payload && payload.length > 0) {
                  const data = payload[0].payload;
                  return data.fullDate || label;
                }
                return label;
              }}
              formatter={(value: number, name: string) => {
                if (name === 'profitMargin') {
                  return [`${value.toFixed(0)}%`, '손익률 (%)'];
                }
                if (name === 'revenue') {
                  return [`${value.toFixed(0)}k`, '매출 (K)'];
                }
                return [value, name];
              }}
            />
            <Legend />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="profitMargin"
              stroke="#4CAF50"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorProfitMargin)"
              name="손익률 (%)"
            />
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="revenue"
              stroke="#FF9800"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorRevenue)"
              name="매출 (k)"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

